/**
 * URL contract for saved-view display options. Params are the wire diff over the saved
 * baseline: absent inherits the baseline, and setting a param equal to the baseline
 * deletes it. The server validates the diff and may fall back on an unsupported value.
 * One writer covers every managed axis; sort will extend the shape.
 */

export const LAYOUT_PARAM = 'layout'
export const GROUP_BY_PARAM = 'group_by'
export const SWIMLANE_BY_PARAM = 'swimlane_by'
const PAGE_PARAM = 'page'

export const LAYOUTS = ['list', 'board'] as const
export type Layout = (typeof LAYOUTS)[number]

export interface SavedViewUrlParams {
  layout: Layout
  groupBy: string | null
  /** Omitted by hosts that do not manage swimlane URL overrides. */
  swimlaneBy?: string | null
}

export interface SavedViewUrlOverrides {
  layout?: Layout
  groupBy?: string
  swimlaneBy?: string
}

function isLayout(value: string | null): value is Layout {
  return value !== null && (LAYOUTS as readonly string[]).includes(value)
}

export function readSavedViewUrlOverrides(search: URLSearchParams): SavedViewUrlOverrides {
  const rawLayout = search.get(LAYOUT_PARAM)
  const groupBy = search.get(GROUP_BY_PARAM)?.trim() || null
  const swimlaneBy = search.get(SWIMLANE_BY_PARAM)?.trim() || null
  return {
    ...(isLayout(rawLayout) ? {layout: rawLayout} : {}),
    ...(groupBy ? {groupBy} : {}),
    ...(swimlaneBy ? {swimlaneBy} : {}),
  }
}

/**
 * Returns updated params after applying a partial change to the resolved shape. Each key
 * in `changes` is treated as the new effective value for that axis; keys you omit are
 * left as-is. `groupBy: null` reverts to the baseline (a board without grouping is not a
 * URL-authorable state — the user switches to list instead).
 *
 * Cross-axis rules baked in: layout=list drops managed board axes, and
 * pagination resets when any effective value actually changes.
 */
export function writeSavedViewOverrides(
  search: URLSearchParams,
  changes: Partial<SavedViewUrlParams>,
  baseline: SavedViewUrlParams,
  current: SavedViewUrlParams,
): URLSearchParams {
  const currentLayout = current.layout
  const currentGroupBy = current.groupBy
  const nextLayout = changes.layout ?? currentLayout
  const nextGroupBy = 'groupBy' in changes ? (changes.groupBy ?? baseline.groupBy) : currentGroupBy
  const managesSwimlanes = baseline.swimlaneBy !== undefined
  const nextSwimlaneBy = 'swimlaneBy' in changes ? changes.swimlaneBy : current.swimlaneBy

  const next = new URLSearchParams(search)
  writeOrClear(next, LAYOUT_PARAM, nextLayout, baseline.layout)
  if (nextLayout === 'board') {
    writeOrClear(next, GROUP_BY_PARAM, nextGroupBy, baseline.groupBy)
  } else {
    next.delete(GROUP_BY_PARAM)
  }
  if (managesSwimlanes) {
    if (nextLayout === 'board') {
      writeOrClear(next, SWIMLANE_BY_PARAM, nextSwimlaneBy ?? null, baseline.swimlaneBy ?? null)
    } else {
      next.delete(SWIMLANE_BY_PARAM)
    }
  }

  const effectiveGroupBy = nextLayout === 'board' ? nextGroupBy : null
  const previousGroupBy = currentLayout === 'board' ? currentGroupBy : null
  const effectiveSwimlaneBy = nextLayout === 'board' ? (nextSwimlaneBy ?? null) : null
  const previousSwimlaneBy = currentLayout === 'board' ? (current.swimlaneBy ?? null) : null
  if (
    nextLayout !== currentLayout ||
    effectiveGroupBy !== previousGroupBy ||
    (managesSwimlanes && effectiveSwimlaneBy !== previousSwimlaneBy)
  )
    next.delete(PAGE_PARAM)
  return next
}

// Applies delete-when-equal-to-baseline for one param.
function writeOrClear(params: URLSearchParams, key: string, value: string | null, baseline: string | null): void {
  if (value === null || value === baseline) params.delete(key)
  else params.set(key, value)
}
